import React from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import AppIcon from '../ui/AppIcon';
import { FaPlay, FaStar, FaUsers, FaApple } from 'react-icons/fa';
import { FaMusic, FaVideo, FaOm, FaWandMagicSparkles } from 'react-icons/fa6';

const HomePage = () => {
  const { user } = useAuth();

  return (
    <div className="home-page">
      {/* Hero Section */}
      <section className="hero">
        <div className="container">
          <div className="hero-content">
            <div className="hero-text">
              <h1 className="hero-title">
                Nurture Your Inner Journey of 
                <span className="mindfulness-text"> Mindfulness</span>
              </h1>
              <p className="hero-subtitle">
                Discover the path to self-awareness through guided chapters, 
                interactive exercises, and personalized mindfulness practices.
              </p>
              <div className="hero-actions">
                {user ? (
                  <Link to="/chapters" className="btn btn-primary btn-large">
                    <FaPlay />
                    Continue Your Journey
                  </Link>
                ) : (
                  <>
                    <Link to="/signup" className="btn btn-primary btn-large">
                      Start Your Journey
                    </Link>
                    <Link to="/chapters" className="btn btn-outline btn-large">
                      Preview Chapters
                    </Link>
                  </>
                )}
              </div>
            </div>
            <div className="hero-visual">
              <AppIcon size={120} className="hero-icon" />
              <div className="floating-elements">
                <div className="floating-leaf"></div>
                <div className="floating-leaf"></div>
                <div className="floating-leaf"></div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="features">
        <div className="container">
          <h2 className="section-title">Transform Your Inner World</h2>
          <div className="features-grid">
            <div className="feature-card">
              <div className="feature-icon mindfulness-gradient">
                <FaStar />
              </div>
              <h3>Guided Chapters</h3>
              <p>Step-by-step mindfulness content designed to guide you through your inner journey.</p>
            </div>
            
            <div className="feature-card">
              <div className="feature-icon mindfulness-gradient">
                <FaPlay />
              </div>
              <h3>Interactive Exercises</h3>
              <p>Practical exercises and reflections to deepen your understanding and practice.</p>
            </div>
            
            <div className="feature-card">
              <div className="feature-icon mindfulness-gradient">
                <FaUsers />
              </div>
              <h3>Personal Growth</h3>
              <p>Track your progress and build lasting mindfulness habits with our structured approach.</p>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="content-preview">
        <div className="container">
          <h2 className="section-title">How It Works</h2>
          <div className="tiers-grid">
            <div className="tier-card">
              <h3>Guest Access</h3>
              <ul>
                <li>Browse all experiences</li>
                <li>Read chapter overviews</li>
                <li>Preview mindfulness topics</li>
              </ul>
              <Link to="/chapters" className="btn btn-outline">Browse Chapters</Link>
            </div>
            
            <div className="tier-card featured">
              <div className="featured-badge">Most Popular</div>
              <h3>Full Access (Free)</h3>
              <ul>
                <li>Detailed chapter content</li>
                <li>Interactive exercises</li>
                <li>Answer explanations</li>
                <li>Progress tracking</li>
              </ul>
              <Link to="/signup" className="btn btn-primary">Sign In to Unlock</Link>
            </div>
          </div>
        </div>
      </section>

      {/* Media Showcase */}
      <section className="media-showcase">
        <div className="container">
          <h2 className="section-title">Songs, Videos & Guided Meditations</h2>
          <p className="section-subtitle">
            Listen, watch, and reflect — or create something entirely your own.
          </p>
          <div className="media-showcase-grid">
            <Link to="/media?category=songs" className="media-showcase-card">
              <div className="media-showcase-icon songs"><FaMusic /></div>
              <h3>Songs</h3>
              <p>Curated tracks for your inner journey</p>
            </Link>
            <Link to="/media?category=videos" className="media-showcase-card">
              <div className="media-showcase-icon videos"><FaVideo /></div>
              <h3>Videos</h3>
              <p>Inspirational and mindfulness videos</p>
            </Link>
            <Link to="/media?category=meditations" className="media-showcase-card">
              <div className="media-showcase-icon meditations"><FaOm /></div>
              <h3>Meditations</h3>
              <p>Guided practices to help you find calm</p>
            </Link>
            <Link to="/create-song" className="media-showcase-card featured">
              <div className="media-showcase-icon create"><FaWandMagicSparkles /></div>
              <h3>Create a Song</h3>
              <p>Turn your story into a personalized song</p>
            </Link>
          </div>
          <div className="media-showcase-cta">
            <Link to="/media" className="btn btn-outline">Explore All Media</Link>
          </div>
        </div>
      </section>

      {/* Call to Action */}
      <section className="cta">
        <div className="container">
          <div className="cta-content">
            <AppIcon size={60} />
            <h2>Ready to Begin Your Mindfulness Journey?</h2>
            <p>Begin your journey of mindfulness and self-awareness.</p>
            {!user && (
              <Link to="/signup" className="btn btn-primary btn-large">
                Create Free Account
              </Link>
            )}
            <div className="mobile-app-links">
              <p>Also available on mobile:</p>
              <div className="app-badges">
                <a href="https://apps.apple.com/us/app/esscape/id6749832973" className="app-badge" target="_blank" rel="noopener noreferrer">
                  <FaApple size={20} />
                  <div>
                    <span style={{ fontSize: '10px', display: 'block', lineHeight: 1 }}>Download on the</span>
                    <span style={{ fontSize: '16px', fontWeight: 'bold', display: 'block', lineHeight: 1.2 }}>App Store</span>
                  </div>
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default HomePage;
